import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { formatRange } from "@/lib/format";
import { Heart, X } from "lucide-react";

export const Route = createFileRoute("/_authenticated/membros/favoritos")({
  head: () => ({ meta: [{ title: "Favoritos — Projeto 101" }, { name: "robots", content: "noindex, nofollow" }] }),
  component: FavoritesPage,
});

function FavoritesPage() {
  const qc = useQueryClient();
  const [compare, setCompare] = useState<string[]>([]);

  const { data: favs } = useQuery({
    queryKey: ["favorites-full"],
    queryFn: async () => {
      const { data } = await supabase
        .from("favorites")
        .select("business:businesses(id, slug, title, summary, category:categories(name), investment_min_cents, investment_max_cents, ticket_cents, difficulty, scalability, operation_type, first_step)")
        .order("created_at", { ascending: false });
      return (data ?? []).map((f: any) => f.business).filter(Boolean);
    },
  });

  async function remove(id: string) {
    const { data: userRes } = await supabase.auth.getUser();
    if (!userRes.user) return;
    await supabase.from("favorites").delete().eq("user_id", userRes.user.id).eq("business_id", id);
    qc.invalidateQueries({ queryKey: ["favorites-full"] });
    qc.invalidateQueries({ queryKey: ["favorite-ids"] });
    setCompare((c) => c.filter((x) => x !== id));
  }

  function toggleCompare(id: string) {
    setCompare((c) => (c.includes(id) ? c.filter((x) => x !== id) : c.length < 3 ? [...c, id] : c));
  }

  const compareList = (favs ?? []).filter((b: any) => compare.includes(b.id));

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-3xl font-extrabold">Favoritos</h1>
        <p className="mt-1 text-sm text-[var(--gray-ink)]">
          Selecione até 3 negócios para comparar lado a lado.
        </p>
      </div>

      {(!favs || favs.length === 0) && (
        <p className="rounded-2xl border border-black/5 bg-white p-6 text-sm text-[var(--gray-ink)]">
          Você ainda não favoritou nenhum negócio.{" "}
          <Link to="/membros/negocios" className="text-[var(--green)] hover:underline">Ver biblioteca</Link>
        </p>
      )}

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {(favs ?? []).map((b: any) => (
          <div key={b.id} className="flex flex-col rounded-2xl border border-black/5 bg-white p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-[var(--green)]">{b.category?.name}</p>
                <h3 className="mt-1 font-display text-lg font-extrabold">{b.title}</h3>
              </div>
              <button onClick={() => remove(b.id)} className="text-[var(--gray-ink)] hover:text-red-600" aria-label="Remover">
                <X size={16} />
              </button>
            </div>
            <p className="mt-2 line-clamp-2 text-sm text-[var(--gray-ink)]">{b.summary}</p>
            <div className="mt-auto flex items-center gap-2 pt-4">
              <Link to="/membros/negocios/$slug" params={{ slug: b.slug }} className="rounded-md bg-[var(--navy)] px-3 py-1.5 text-xs font-semibold text-white">
                Abrir ficha
              </Link>
              <button
                onClick={() => toggleCompare(b.id)}
                className={`rounded-md px-3 py-1.5 text-xs font-semibold ${
                  compare.includes(b.id) ? "bg-[var(--green)] text-white" : "border border-black/10"
                }`}
              >
                {compare.includes(b.id) ? "Selecionado" : "Comparar"}
              </button>
            </div>
          </div>
        ))}
      </div>

      {compareList.length >= 2 && (
        <section className="rounded-2xl border border-black/5 bg-white p-6">
          <h2 className="font-display text-xl font-extrabold">Comparação</h2>
          <div className="mt-4 overflow-x-auto">
            <table className="w-full min-w-[600px] text-sm">
              <thead>
                <tr className="border-b border-black/5 text-left text-xs uppercase tracking-wide text-[var(--gray-ink)]">
                  <th className="py-2 pr-4">Atributo</th>
                  {compareList.map((b: any) => (
                    <th key={b.id} className="py-2 pr-4 font-display text-sm text-[var(--navy)]">{b.title}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <CmpRow label="Categoria" values={compareList.map((b: any) => b.category?.name)} />
                <CmpRow label="Investimento" values={compareList.map((b: any) => formatRange(b.investment_min_cents, b.investment_max_cents))} />
                <CmpRow label="Dificuldade" values={compareList.map((b: any) => b.difficulty)} />
                <CmpRow label="Escalabilidade" values={compareList.map((b: any) => b.scalability)} />
                <CmpRow label="Operação" values={compareList.map((b: any) => b.operation_type)} />
                <CmpRow label="Primeiro passo" values={compareList.map((b: any) => b.first_step)} />
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}

function CmpRow({ label, values }: { label: string; values: (string | null | undefined)[] }) {
  return (
    <tr className="border-b border-black/5 last:border-0">
      <td className="py-2 pr-4 font-semibold text-[var(--gray-ink)]">{label}</td>
      {values.map((v, i) => (
        <td key={i} className="py-2 pr-4 capitalize">{v ?? "—"}</td>
      ))}
    </tr>
  );
}
